import { Link, useParams } from 'react-router-dom';
import { usePageMeta } from '@/hooks/usePageMeta';
import { getSpecialById, getNewsById, getVideoById, specials, formatDate } from '@/utils/content';
import Breadcrumb from '@/components/feature/Breadcrumb';
import NewsCard from '@/components/feature/NewsCard';
import VideoCard from '@/components/feature/VideoCard';

const container = 'w-full max-w-[1280px] mx-auto px-4 md:px-8';

export default function SpecialDetailPage() {
  const { id = '' } = useParams();
  const item = getSpecialById(id);

  usePageMeta(
    item ? `${item.title} - 热点专题 - 91.con` : '热点专题 - 91.con',
    item ? item.intro : '91.con 热点专题页',
    item ? `${item.tags.join(',')},热点专题,91.con,娱乐资讯` : '热点专题,91.con'
  );

  if (!item) {
    return (
      <section className={`${container} py-20`}>
        <div className="text-center max-w-lg mx-auto">
          <i className="ri-article-line text-5xl text-foreground-500" />
          <h1 className="font-heading text-2xl font-bold text-foreground-950 mt-4">未找到该专题</h1>
          <p className="text-sm text-foreground-600 mt-2">专题可能已下线或链接有误，去首页看看其他内容吧。</p>
          <Link
            to="/"
            className="mt-6 inline-flex h-11 px-6 items-center rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </section>
    );
  }

  const specialNews = item.newsIds.map((nid) => getNewsById(nid)).filter((n) => n !== undefined);
  const specialVideos = item.videoIds.map((vid) => getVideoById(vid)).filter((v) => v !== undefined);
  const otherSpecials = specials.filter((s) => s.id !== item.id).slice(0, 3);

  return (
    <>
      <section className="relative w-full h-[320px] md:h-[420px] overflow-hidden">
        <img
          src={item.cover}
          alt={item.title}
          title={`${item.title} - 91.con 热点专题`}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/70 to-background-50/40" />
        <div className={`${container} absolute inset-0 flex flex-col justify-end pb-8 md:pb-10`}>
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-primary-500 text-background-50 text-xs font-medium">
              <i className="ri-fire-line" />热点专题
            </span>
            <h1 className="font-heading text-2xl md:text-4xl font-extrabold text-foreground-950 mt-3 leading-snug">
              {item.title}
            </h1>
            <p className="text-sm md:text-base text-foreground-700 mt-3 leading-relaxed line-clamp-3">{item.intro}</p>
            <div className="flex items-center flex-wrap gap-4 mt-4 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5"><i className="ri-article-line" />{specialNews.length} 篇资讯</span>
              <span className="flex items-center gap-1.5"><i className="ri-play-list-line" />{specialVideos.length} 个视频</span>
              <span className="flex items-center gap-1.5"><i className="ri-refresh-line" />更新于 {formatDate(item.updateDate)}</span>
            </div>
          </div>
        </div>
      </section>

      <section className={`${container} py-8 md:py-10`}>
        <Breadcrumb items={[{ label: '热点专题' }, { label: item.title }]} />

        <div className="mt-7 p-5 md:p-6 rounded-lg bg-background-100 border-l-4 border-l-accent-500 border border-background-200">
          <div className="flex items-center gap-2 mb-2.5">
            <i className="ri-quill-pen-line text-accent-500 text-lg" />
            <h2 className="font-heading text-base font-bold text-foreground-950">编辑点评</h2>
          </div>
          <p className="text-sm md:text-base text-foreground-700 leading-loose">{item.editorNote}</p>
          <div className="flex flex-wrap items-center gap-2 mt-4">
            {item.tags.map((t) => (
              <span key={t} className="px-3 py-1 rounded-full bg-accent-100/80 border border-accent-300/50 text-accent-900 text-xs">
                #{t}
              </span>
            ))}
          </div>
        </div>

        <div className="mt-12">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 flex items-center gap-2.5 mb-6">
            <span className="w-1 h-6 rounded-full bg-primary-500" />
            专题资讯
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {specialNews.map((n) => (
              <NewsCard key={n.id} item={n} />
            ))}
          </div>
        </div>

        <div className="mt-12">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 flex items-center gap-2.5 mb-6">
            <span className="w-1 h-6 rounded-full bg-accent-500" />
            专题视频
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {specialVideos.map((v) => (
              <VideoCard key={v.id} item={v} />
            ))}
          </div>
        </div>

        <div className="mt-14">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 flex items-center gap-2.5 mb-6">
            <span className="w-1 h-6 rounded-full bg-primary-500" />
            相关专题
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {otherSpecials.map((s) => (
              <Link
                key={s.id}
                to={`/special/${s.id}`}
                className="group flex items-center gap-4 p-4 rounded-lg bg-background-100 border border-background-200 hover:border-primary-500/60 transition-colors cursor-pointer"
              >
                <div className="w-20 h-14 rounded-md overflow-hidden shrink-0">
                  <img src={s.cover} alt={s.title} title={s.title} className="w-full h-full object-cover object-top" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-400 transition-colors line-clamp-2 leading-snug">
                    {s.title}
                  </h3>
                  <span className="text-xs text-foreground-500 mt-1 inline-block">
                    {s.newsIds.length + s.videoIds.length} 条内容
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}